import { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../services/auth.service";
import { dataLayerGuardRequest, getCachedResponse, loadRequest } from "./homeRequests";

/**
 * Fetches the DataLayer Guard dashboard data (errors and error timeline) for a period
 *
 * @param {object} period - First day, last day and window of the request, see getPeriod on the homepage
 * @param {boolean} enabled - Only fetch when true
 * @param {string} scope - Part of the homepage that uses the data, for the log prefix (e.g. "Home Quick actions")
 * @returns {object|null|undefined} Response context, undefined while loading or disabled and null when the
 *   request failed
 */
export const useDataLayerGuardData = (period, enabled, scope) => {
  const { companyId } = useContext(AuthContext);
  const [result, setResult] = useState();

  const request = useMemo(() => dataLayerGuardRequest({ companyId, period }), [companyId, period]);

  // Only the response of this request (company and period), from the cache when it was loaded in the last minutes
  const cached = enabled ? getCachedResponse(request.key) : undefined;

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;
    loadRequest(request)
      .then((context) => {
        if (!cancelled) setResult({ key: request.key, context });
      })
      .catch((error) => {
        console.error(`[company ${companyId}] [${scope}] Failed to load the DataLayer Guard errors:`, error);
        if (!cancelled) setResult({ key: request.key, context: null });
      });

    // Ignore a response that arrives after the company or the period changed
    return () => {
      cancelled = true;
    };
  }, [request, enabled, companyId, scope]);

  if (!enabled) return undefined;
  return result?.key === request.key ? result.context : cached;
};
